import {useCallback, useEffect, useRef} from 'react';

export const PRODUCT_AUTOSAVE_DELAY = 750;

const pendingFlushes = new Set();
let mountedAutosaves = 0;

const flushPendingAutosaves = () => Promise.all(
    [...pendingFlushes].map(flush => flush()),
);

const flushPendingAutosavesForPagehide = () => {
    Promise.allSettled([...pendingFlushes].map(flush => flush()));
};

const handleProductSubnavClick = event => {
    if (event.defaultPrevented || event.button !== 0
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
    }

    const link = event.target.closest?.('.product-subnav a[href]');
    if (!link || link.target === '_blank' || pendingFlushes.size === 0) return;

    event.preventDefault();
    flushPendingAutosaves()
        .then(() => window.location.assign(link.href))
        .catch(() => undefined);
};

const addNavigationGuards = () => {
    if (typeof document === 'undefined') return;
    if (mountedAutosaves === 0) {
        document.addEventListener('click', handleProductSubnavClick, true);
        window.addEventListener('pagehide', flushPendingAutosavesForPagehide);
    }
    mountedAutosaves += 1;
};

const removeNavigationGuards = () => {
    if (typeof document === 'undefined') return;
    mountedAutosaves -= 1;
    if (mountedAutosaves === 0) {
        document.removeEventListener('click', handleProductSubnavClick, true);
        window.removeEventListener('pagehide', flushPendingAutosavesForPagehide);
    }
};

/**
 * Debounce saves while keeping the latest payload available for an unmount flush.
 *
 * @param {Object|null} payload
 * @param {Function|undefined} onAutosave
 * @param {Object} options
 * @returns {{cancelAutosave: Function, flushAutosave: Function}}
 */
export default function useDebouncedAutosave(
    payload,
    onAutosave,
    {delay = PRODUCT_AUTOSAVE_DELAY, enabled = true} = {},
) {
    const onAutosaveRef = useRef(onAutosave);
    const payloadRef = useRef(payload);
    const payloadKey = JSON.stringify(payload);
    const previousPayloadKeyRef = useRef(payloadKey);
    const timeoutRef = useRef(null);
    const pendingRef = useRef(false);
    const flushAutosaveRef = useRef(null);
    const mountedRef = useRef(true);

    onAutosaveRef.current = onAutosave;
    payloadRef.current = payload;

    const cancelAutosave = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        pendingRef.current = false;
        pendingFlushes.delete(flushAutosaveRef.current);
    }, []);

    const flushAutosave = useCallback(() => {
        if (!pendingRef.current || !onAutosaveRef.current) return Promise.resolve();

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        pendingRef.current = false;
        pendingFlushes.delete(flushAutosaveRef.current);
        return Promise.resolve(onAutosaveRef.current(payloadRef.current))
            .catch(error => {
                if (mountedRef.current) {
                    pendingRef.current = true;
                    pendingFlushes.add(flushAutosaveRef.current);
                }
                throw error;
            });
    }, []);
    flushAutosaveRef.current = flushAutosave;

    useEffect(() => {
        if (payloadKey === previousPayloadKeyRef.current) return;
        previousPayloadKeyRef.current = payloadKey;

        if (!enabled || !onAutosave || payload === null) {
            cancelAutosave();
            return;
        }

        pendingRef.current = true;
        pendingFlushes.add(flushAutosave);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            flushAutosave().catch(() => undefined);
        }, delay);
    }, [
        cancelAutosave,
        delay,
        enabled,
        flushAutosave,
        onAutosave,
        payload,
        payloadKey,
    ]);

    useEffect(() => {
        mountedRef.current = true;
        addNavigationGuards();
        return () => {
            // The embedded wizard is often unmounted by the EJS product subnav.
            // Start the latest save before teardown; callers use keepalive where supported.
            flushAutosave().catch(() => undefined);
            mountedRef.current = false;
            pendingFlushes.delete(flushAutosave);
            removeNavigationGuards();
        };
    }, [flushAutosave]);

    return {cancelAutosave, flushAutosave};
}
